import { createTheme } from '@mui/material/styles';

// ============================================================================
// COLOR PALETTE
// ============================================================================
// Define your brand colors and semantic colors here
const colors = {
  // Primary brand colors
  primary: {
    main: '#35677f',      // Deep blue
    light: '#5a8bb8',     // Lighter blue
    dark: '#00374b',      // Darker blue
    contrastText: '#ffffff',
  },
  // Secondary accent colors
  secondary: {
    main: '#fbba00',      // Coral/salmon
    light: '#ff9e9e',     // Light coral
    dark: '#f08300',      // Dark coral
    contrastText: '#ffffff',
  },
  // Success, error, warning, info
  success: {
    main: '#66bb6a',
    light: '#98ee99',
    dark: '#338a3e',
  },
  error: {
    main: '#f44336',
    light: '#ff7961',
    dark: '#ba000d',
  },
  warning: {
    main: '#ffa726',
    light: '#ffd95b',
    dark: '#c77800',
  },
  info: {
    main: '#29b6f6',
    light: '#73e8ff',
    dark: '#0086c3',
  },
  // Neutral colors
  grey: {
    50: '#fafafa',
    100: '#f5f5f5',
    200: '#eeeeee',
    300: '#e0e0e0',
    400: '#bdbdbd',
    500: '#9e9e9e',
    600: '#757575',
    700: '#616161',
    800: '#424242',
    900: '#212121',
  },
  // Background colors
  background: {
    default: '#fafafa',
    paper: '#ffffff',
    elevated: '#f5f5f5',  // For cards/elevated surfaces
  },
  // Text colors
  text: {
    primary: 'rgba(0, 0, 0, 0.87)',
    secondary: 'rgba(0, 0, 0, 0.6)',
    disabled: 'rgba(0, 0, 0, 0.38)',
  },
  // Custom semantic colors for your app
  chunk: {
    selected: '#8fbcbb',
    selectedBorder: '#81a1c1',
    inChunk: '#d8dee9',
    inChunkBorder: '#a3be8c',
    hover: '#e5e9f0',
  },
};

// ============================================================================
// TYPOGRAPHY
// ============================================================================
const typography = {
  fontFamily: [
    '-apple-system',
    'BlinkMacSystemFont',
    '"Segoe UI"',
    'Roboto',
    '"Helvetica Neue"',
    'Arial',
    'sans-serif',
    '"Apple Color Emoji"',
    '"Segoe UI Emoji"',
    '"Segoe UI Symbol"',
  ].join(','),
  
  // Font sizes (in rem)
  fontSize: 14,
  
  // Font weights
  fontWeightLight: 300,
  fontWeightRegular: 400,
  fontWeightMedium: 500,
  fontWeightBold: 700,
  
  // Heading styles
  h1: {
    fontSize: '2.5rem',
    fontWeight: 700,
    lineHeight: 1.2,
    letterSpacing: '-0.01562em',
  },
  h2: {
    fontSize: '2rem',
    fontWeight: 700,
    lineHeight: 1.3,
    letterSpacing: '-0.00833em',
  },
  h3: {
    fontSize: '1.75rem',
    fontWeight: 600,
    lineHeight: 1.4,
  },
  h4: {
    fontSize: '1.5rem',
    fontWeight: 600,
    lineHeight: 1.4,
  },
  h5: {
    fontSize: '1.25rem',
    fontWeight: 600,
    lineHeight: 1.5,
  },
  h6: {
    fontSize: '1rem',
    fontWeight: 600,
    lineHeight: 1.5,
  },
  
  // Body text
  body1: {
    fontSize: '1rem',
    lineHeight: 1.5,
  },
  body2: {
    fontSize: '0.875rem',
    lineHeight: 1.43,
  },
  
  // Button text
  button: {
    fontSize: '0.875rem',
    fontWeight: 500,
    textTransform: 'none', // Don't uppercase buttons by default
  },
  
  // Caption and overline
  caption: {
    fontSize: '0.75rem',
    lineHeight: 1.66,
  },
  overline: {
    fontSize: '0.75rem',
    fontWeight: 500,
    textTransform: 'uppercase',
    letterSpacing: '0.08333em',
  },
};

// ============================================================================
// SPACING & LAYOUT
// ============================================================================
const spacing = 8; // Base spacing unit in pixels (8px)

const shape = {
  borderRadius: 8, // Default border radius for components
};

// ============================================================================
// SHADOWS
// ============================================================================
// Custom shadows for depth
const shadows = [
  'none',
  '0px 2px 4px rgba(0, 0, 0, 0.05)',
  '0px 4px 8px rgba(0, 0, 0, 0.08)',
  '0px 8px 16px rgba(0, 0, 0, 0.1)',
  '0px 12px 24px rgba(0, 0, 0, 0.12)',
  '0px 16px 32px rgba(0, 0, 0, 0.14)',
  // Add more if needed...
];

// ============================================================================
// BREAKPOINTS
// ============================================================================
const breakpoints = {
  values: {
    xs: 0,
    sm: 600,
    md: 960,
    lg: 1280,
    xl: 1920,
  },
};

// ============================================================================
// Z-INDEX
// ============================================================================
const zIndex = {
  mobileStepper: 1000,
  fab: 1050,
  speedDial: 1050,
  appBar: 1100,
  drawer: 1200,
  modal: 1300,
  snackbar: 1400,
  tooltip: 1500,
};

// ============================================================================
// COMPONENT OVERRIDES
// ============================================================================
const components = {
  // Button overrides
  MuiButton: {
    styleOverrides: {
      root: {
        borderRadius: 8,
        textTransform: 'none',
        fontWeight: 500,
        padding: '10px 24px',
        boxShadow: 'none',
        '&:hover': {
          boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.12)',
        },
      },
      contained: {
        boxShadow: 'none',
        '&:hover': {
          boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.15)',
        },
      },
      sizeSmall: {
        padding: '6px 16px',
        fontSize: '0.8125rem',
      },
      sizeLarge: {
        padding: '12px 28px',
        fontSize: '0.9375rem',
      },
    },
    defaultProps: {
      disableElevation: true,
    },
  },

  // Paper overrides
  MuiPaper: {
    styleOverrides: {
      root: {
        backgroundImage: 'none',
      },
      rounded: {
        borderRadius: 12,
      },
      elevation1: {
        boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.05)',
      },
      elevation2: {
        boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.08)',
      },
      elevation3: {
        boxShadow: '0px 8px 16px rgba(0, 0, 0, 0.1)',
      },
    },
  },

  // Card overrides
  MuiCard: {
    styleOverrides: {
      root: {
        borderRadius: 12,
        boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.08)',
        '&:hover': {
          boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.12)',
        },
      },
    },
  },

  // TextField/Input overrides
  MuiTextField: {
    defaultProps: {
      variant: 'outlined',
    },
  },
  MuiOutlinedInput: {
    styleOverrides: {
      root: {
        borderRadius: 8,
        '&:hover .MuiOutlinedInput-notchedOutline': {
          borderColor: colors.primary.light,
        },
      },
    },
  },

  // Chip overrides
  MuiChip: {
    styleOverrides: {
      root: {
        borderRadius: 8,
        fontWeight: 500,
      },
    },
  },

  // Divider overrides
  MuiDivider: {
    styleOverrides: {
      root: {
        borderColor: colors.grey[200],
      },
    },
  },

  // AppBar overrides
  MuiAppBar: {
    styleOverrides: {
      root: {
        boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.08)',
      },
    },
    defaultProps: {
      elevation: 0,
    },
  },

  // Drawer overrides
  MuiDrawer: {
    styleOverrides: {
      paper: {
        boxShadow: 'none',
      },
    },
  },

  // List overrides
  // NOTE: ChunkList overrides these colors locally to use chunk palette.
  // This global override applies to other list buttons that need primary branding.
  MuiListItemButton: {
    styleOverrides: {
      root: {
        '&.Mui-selected': {
          backgroundColor: colors.primary.main,
          color: colors.primary.contrastText,
          '&:hover': {
            backgroundColor: colors.primary.dark,
          },
          '& .MuiListItemIcon-root': {
            color: colors.primary.contrastText,
          },
          '& .MuiListItemText-secondary': {
            color: colors.secondary.contrastText,
          },
        },
      },
    },
  },
  
  // Dialog overrides
  MuiDialog: {
    styleOverrides: {
      paper: {
        borderRadius: 16,
      },
    },
  },

  // Tooltip overrides
  MuiTooltip: {
    styleOverrides: {
      tooltip: {
        backgroundColor: colors.grey[800],
        fontSize: '0.75rem',
        borderRadius: 6,
        padding: '8px 12px',
      },
      arrow: {
        color: colors.grey[800],
      },
    },
  },
};

// ============================================================================
// CREATE THEME
// ============================================================================

// First, create the base theme
const theme = createTheme({
  palette: {
    primary: colors.primary,
    secondary: colors.secondary,
    success: colors.success,
    error: colors.error,
    warning: colors.warning,
    info: colors.info,
    grey: colors.grey,
    background: colors.background,
    text: colors.text,
  },
  typography,
  spacing,
  shape,
  breakpoints,
  zIndex,
  components,
  shadows: shadows,
});

// Add custom palette colors by augmenting the theme
theme.palette.chunk = colors.chunk;

export default theme;

// Export individual pieces if needed elsewhere
export { colors, typography, spacing, shape, breakpoints, zIndex };